/**
 * Ministers List Report Page
 * Complete list of ministers in credential requests with advanced filters
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useUserProfile } from "@/features/ministers/hooks/useUserProfile";
import { useMinistersListReport } from "../hooks/useMinistersListReport";
import { ReportFilters } from "../components/ReportFilters";
import { SummaryCards } from "../components/SummaryCards";
import { DegreeBreakdownTable } from "../components/DegreeBreakdownTable";
import { MinistersReportTable } from "../components/MinistersReportTable";
import { UncredentialedMinistersTable } from "../components/UncredentialedMinistersTable";
import { exportMinistersListToExcel } from "../utils/excelExport";
import { getAllDistrictsWithRegions } from "@/Services/regions.service";
import { getAllDegrees } from "@/Services/degrees.service";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";

export function MinistersListReportPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin } = useUserProfile();

  // Report data and state
  const {
    ministers,
    filters,
    summary,
    systemDegreeCounts,
    uncredentialedMinisters,
    inactivatedMinisters,
    isLoading,
    isLoadingAllMinisters,
    error,
    updateFilter,
    resetFilters,
    toggleCredentialType,
    toggleStatus,
  } = useMinistersListReport();

  // Filter options data
  const [districts, setDistricts] = useState<District[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setIsLoadingOptions(true);
        const [districtsData, degreesData] = await Promise.all([
          getAllDistrictsWithRegions(),
          getAllDegrees(),
        ]);

        setDistricts(districtsData);
        // Extract all regions from districts
        const allRegions = districtsData.flatMap(d => d.regions || []);
        setRegions(allRegions);
        setDegrees(degreesData);
      } catch (err) {
        console.error("Error loading filter options:", err);
        toast({
          title: "Error",
          description: "No se pudieron cargar las opciones de filtros",
          variant: "destructive",
        });
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadFilterOptions();
  }, [toast]);

  // Handle export to Excel
  const handleExport = async () => {
    try {
      await exportMinistersListToExcel(ministers, filters, systemDegreeCounts, uncredentialedMinisters);

      toast({
        title: "Éxito",
        description: "Reporte exportado correctamente",
      });
    } catch (err) {
      console.error("Error exporting report:", err);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte",
        variant: "destructive",
      });
    }
  };

  // Show error if query failed
  if (error) {
    return (
      <div className="space-y-4 p-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/reports")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 size-4" />
          Volver a Reportes
        </Button>
        <div className="flex h-64 items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5">
          <div className="text-center">
            <p className="text-lg font-semibold text-destructive">
              Error al cargar el reporte
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {error instanceof Error ? error.message : "Error desconocido"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate("/reports")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 size-4" />
          Volver a Reportes
        </Button>
        <h1 className="text-3xl font-bold">Lista Completa de Ministros</h1>
        <p className="mt-2 text-muted-foreground">
          Ministros incluidos en solicitudes de credenciales con filtros avanzados
        </p>
      </div>

      {/* Filters */}
      {isLoadingOptions ? (
        <div className="rounded-lg border bg-card p-4">
          <InlineLoader />
        </div>
      ) : (
        <ReportFilters
          filters={filters}
          onFilterChange={updateFilter}
          onToggleCredentialType={toggleCredentialType}
          onToggleStatus={toggleStatus}
          onReset={resetFilters}
          onExport={handleExport}
          districts={districts}
          regions={regions}
          degrees={degrees}
          isLoading={isLoading}
          totalResults={ministers.length}
        />
      )}

      {/* Summary Section: Cards (1/5) and Degree Distribution (4/5) */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-5">
        <div className="lg:col-span-1">
          <SummaryCards summary={summary} isLoading={isLoading} />
        </div>
        <div className="lg:col-span-4">
          <DegreeBreakdownTable ministers={ministers} isLoading={isLoading} systemDegreeCounts={systemDegreeCounts} />
        </div>
      </div>

      {/* Tabs: En Solicitudes / Sin Credencial / Inactivados */}
      <Tabs defaultValue="in-requests">
        <TabsList>
          <TabsTrigger value="in-requests">
            En Solicitudes
            {!isLoading && (
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">
                {ministers.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="uncredentialed">
            Sin Credencial
            {!isLoadingAllMinisters && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                {uncredentialedMinisters.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="inactivated">
            Inactivados con Credencial
            {!isLoading && inactivatedMinisters.length > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-amber-700">
                {inactivatedMinisters.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="in-requests">
          <h2 className="mb-4 mt-4 text-lg font-semibold">Detalle de Ministros</h2>
          <MinistersReportTable
            data={ministers}
            isLoading={isLoading}
            onRowClick={isAdmin ? (m) => navigate(`/ministers/${m.minister_id}`) : undefined}
          />
        </TabsContent>

        <TabsContent value="uncredentialed">
          <div className="mb-4 mt-4">
            <h2 className="text-lg font-semibold">Ministros Sin Credencial</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Ministros activos que no están incluidos en ninguna solicitud con los filtros actuales.
              Los filtros de territorio, grado y búsqueda se aplican aquí también.
            </p>
          </div>
          <UncredentialedMinistersTable
            data={uncredentialedMinisters}
            isLoading={isLoadingAllMinisters}
            onRowClick={isAdmin ? (m) => navigate(`/ministers/${m.id}`) : undefined}
          />
        </TabsContent>

        <TabsContent value="inactivated">
          <div className="mb-4 mt-4">
            <h2 className="text-lg font-semibold">Inactivados después de credencializar</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Ministros que recibieron credencial en el período filtrado pero fueron dados de baja posteriormente.
              No se cuentan en el total de activos.
            </p>
          </div>
          <MinistersReportTable
            data={inactivatedMinisters}
            isLoading={isLoading}
            onRowClick={isAdmin ? (m) => navigate(`/ministers/${m.minister_id}`) : undefined}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
